// F3: arrivals resolve after the whole legacy summon response, never inside placement / sibling summons.
import { midCombatSummon, newMidCombatSummons, resolveSummonBegins, summonBeginsAbilityId, summonBeginsOperationId, summonRoster } from '../janus-engine/combat/summon-begins.js';
import { ecId, ecLean, itemField, record, upper, type Row } from '../janus-engine/ability/context.js';
import { TRIGGER_CONSENT_REASON } from '../janus-engine/ability/trigger-consent.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { abilityTruthReaders, commitZoneEntryVerdict, reportSummonBegins } from './ability-truth.js';
import { actorEncounterFor } from './movement-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { handleEncounterUpdate } from './encounter-update.js';
import { planWithConsent, offerFortunate } from './trigger-consent.js';
const bucket = (e: Row): Row => record(e.encounters) ?? e;
const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
function mayAct(e: Row, s: Row): boolean {
  const uid = upper(window.FateTide?.user?.id), b = bucket(e);
  if (!UUID.test(uid)) return false;
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  const parent = upper(b.encounter_master_parent_id), c = record(ft?.campaignCache?.get(parent) ?? ft?.currentCampaignArr), cb = record(c?.campaigns) ?? c;
  if (upper(b.encounter_created_by_id) === uid || (upper(cb?.campaign_id) === parent && upper(cb?.campaign_gm) === uid) || summonRoster(e).some(r => record(r.permissions)?.campaign === true)) return true;
  const ec = record(s.encounter_creatures), controller = summonRoster(e).find(r => ecId(r) === upper(ec?.controller_encounter_creature_id));
  const ce = record(controller?.encounter_creatures);
  return !!controller && record(controller.permissions)?.player === true
    && (upper(scalar(ce?.encounter_creature_created_by_id)) === uid || upper(ftLayerValue(controller, 'permissioned')) === uid);
}
type ArrivalOutcome = 'settled' | 'retry';
type ArrivalCommit = Parameters<typeof commitZoneEntryVerdict>[0];
/** A drawn arrival retries delivery only, against the original frame. The operation id is summon-derived in the shared commit. */
const heldVerdicts = new Map<string, { input: ArrivalCommit; combat: string; name: string }>();
const attempts = new Map<string, number>(), settledArrivals = new Map<string, string>();
const arrivalInstallations = new Map<string, Promise<void>>();
/** A throw during planning has no verdict to resend; after a draw it must settle. */
const drewThisAttempt = new Set<string>();
function clearArrival(key: string): void {
  heldVerdicts.delete(key); attempts.delete(key); drewThisAttempt.delete(key);
}
function reportFailure(eid: string, sid: string, outcome: 'stale' | 'refused' | 'error' | 'handback', reason: string): void {
  const held = heldVerdicts.get(eid + '|' + sid), e = actorEncounterFor(eid) as Row | null;
  const s = e && summonRoster(e).find(r => ecId(r) === sid);
  const name = held?.name ?? String(s ? ecLean(s, 'encounter_creature_name') ?? itemField(s, 'item_name') ?? 'The summoned creature' : 'The summoned creature');
  reportSummonBegins(held?.input ?? { encounterId: eid, actorId: sid, abilityId: upper(s && ecLean(s, 'item_id')) }, outcome, reason);
  if (typeof displayWarning === 'function') displayWarning(`${name}: summon-begins could not settle (${reason}).`);
}
async function commitArrival(input: ArrivalCommit, combat: string): Promise<ArrivalOutcome> {
  const saved = await commitZoneEntryVerdict(input), { encounterId: eid, actorId: sid, verdict } = input;
  if (saved.reason === 'commit:summon-begins-done') { settledArrivals.set(eid + '|' + sid, combat); return 'settled'; }
  if (saved.outcome === 'stale' || saved.outcome === 'refused') {
    reportFailure(eid, sid, saved.outcome, saved.reason ?? `summon-begins:commit-${saved.outcome}`); return 'settled';
  }
  reportSummonBegins(input, saved.outcome, saved.reason ?? (saved.parked ? 'summon-begins:outbox-owned' : 'summon-begins'));
  if (saved.parked || saved.outcome === 'served') settledArrivals.set(eid + '|' + sid, combat);
  if (saved.parked) return 'settled';
  if (saved.outcome === 'served' && verdict.fortunateOffers?.length) offerFortunate(eid, sid, verdict.fortunateOffers);
  return saved.outcome === 'served' ? 'settled' : 'retry';
}
async function arrived(eid: string, sid: string): Promise<ArrivalOutcome> {
  const key = eid + '|' + sid;
  drewThisAttempt.delete(key);
  let e = actorEncounterFor(eid) as Row | null, s = e && midCombatSummon(e, sid);
  if (!e) return 'retry';
  if (!s) { settledArrivals.delete(key); return 'settled'; }
  if (!mayAct(e, s)) {
    if (heldVerdicts.has(key)) reportFailure(eid, sid, 'refused', 'summon-begins:actor-permission-lost');
    return 'settled';
  }
  const retained = heldVerdicts.get(key), combatNow = upper(bucket(e).active_combat_id);
  if (settledArrivals.get(key) === combatNow) return 'settled';
  settledArrivals.delete(key);
  if (retained) return retained.combat === combatNow ? commitArrival(retained.input, retained.combat) : 'settled';
  // Arrival state and participants are already present in the adopted encounter.
  const combat = upper(bucket(e).active_combat_id);
  const held = e, summon = s, all = new Map(summonRoster(e).map(r => [ecId(r), r])), before = new Map([...all].map(([id, r]) => [id, JSON.parse(JSON.stringify(r)) as Row]));
  const read = abilityTruthReaders(s, e, new Set(all.keys())), random = browserRollSource(); let drawn = 0;
  const roll = { d: (die: number) => { drawn++; drewThisAttempt.add(eid + '|' + sid); return random.d(die); },
    fraction: () => { drawn++; drewThisAttempt.add(eid + '|' + sid); return random.fraction(); } };
  const verdict = await planWithConsent(eid, sid, async consent => {
    await arrivalInstallations.get(key);
    if (settledArrivals.get(key) === combat) return { kind: 'handback' as const, reason: 'summon-begins:already-applied' };
    const now = actorEncounterFor(eid) as Row | null;
    if (!now || !midCombatSummon(now, sid) || !mayAct(now, summon) || upper(bucket(now).active_combat_id) !== combat) return { kind: 'handback' as const, reason: 'summon-begins:stale' };
    // A foreign write during the five-second consent window invalidates the held frame before drawing.
    for (const [id, r] of before) if (JSON.stringify(summonRoster(now).find(m => ecId(m) === id)) !== JSON.stringify(r)) return { kind: 'handback' as const, reason: 'summon-begins:changed-during-consent' };
    // a creature that JOINED during the window is a foreign write too (A15 closing review P2): the held roster would omit it
    if (summonRoster(now).some(m => !before.has(ecId(m)))) return { kind: 'handback' as const, reason: 'summon-begins:changed-during-consent' };
    return resolveSummonBegins(eid, sid, held, all, { ...read, triggerConsent: consent }, roll);
  }, v => v.kind === 'handback' && v.reason.includes(TRIGGER_CONSENT_REASON));
  await arrivalInstallations.get(key);
  if (settledArrivals.get(key) === combat) return 'settled';
  const input = { encounterId: eid, actorId: sid, abilityId: upper(record(summon.encounter_creatures)?.item_id) };
  if (verdict.kind !== 'resolved') {
    // A15 closing review P1: a hand-back AFTER a real draw is never re-planned (fresh dice would fish) — settled, warned
    if (drawn) { reportFailure(eid, sid, 'handback', verdict.kind === 'handback' ? verdict.reason : verdict.message); return 'settled'; }
    reportSummonBegins(input, 'handback', verdict.kind === 'handback' ? verdict.reason : verdict.message);
    // the summon left / the combat moved on: settled; anything else (a foreign write during consent, an unheld input) is tried again
    return verdict.kind === 'handback' && verdict.reason === 'summon-begins:stale' ? 'settled' : 'retry';
  }
  input.abilityId = summonBeginsAbilityId(verdict, summon);
  const command: ArrivalCommit = { ...input, zoneStatusId: '', verdict, before,
    expectedRevs: new Map([...before].map(([id, r]) => [id, Number(ecLean(r, 'movement_rev'))])), summonBegins: { summonId: sid } };
  if (drawn) heldVerdicts.set(key, { input: command, combat,
    name: String(ecLean(summon, 'encounter_creature_name') ?? itemField(summon, 'item_name') ?? 'The summoned creature') });
  return commitArrival(command, combat);
}
const queues = new Map<string, Promise<string[]>>();
/** Resolves with the ids that must be tried again (each id is caught on its own — one throw never aborts the rest of the batch). */
export function fireSummonBegins(eid: string, ids: readonly string[]): Promise<string[]> {
  eid = upper(eid);
  const run = async (): Promise<string[]> => {
    const again: string[] = [];
    for (const id of new Set(ids.map(upper))) {
      const key = eid + '|' + id;
      let outcome: ArrivalOutcome;
      try { await arrivalInstallations.get(key); outcome = await arrived(eid, id); }
      catch (error) {
        if (heldVerdicts.has(key) || !drewThisAttempt.has(key)) outcome = 'retry';
        else { reportFailure(eid, id, 'error', `summon-begins:plan-threw-after-draw:${String(error)}`); outcome = 'settled'; }
      }
      const n = (attempts.get(key) ?? 0) + 1;
      if (outcome === 'retry' && n < 6) { attempts.set(key, n); again.push(id); }
      else {
        if (outcome === 'retry') reportFailure(eid, id, 'error', 'summon-begins:attempts-exhausted');
        // Keep a combat-scoped settlement marker even after releasing the verdict:
        // a duplicate observation cannot give a failed drawn arrival fresh dice.
        const retained = heldVerdicts.get(key), e = actorEncounterFor(eid) as Row | null;
        if (retained || drewThisAttempt.has(key)) {
          const combat = retained?.combat ?? (e && upper(bucket(e).active_combat_id));
          if (combat && e && midCombatSummon(e, id) && upper(bucket(e).active_combat_id) === combat) settledArrivals.set(key, combat);
        }
        clearArrival(key);
      }
    }
    return again;
  };
  const next = (queues.get(eid) ?? Promise.resolve([] as string[])).catch(() => [] as string[])
    .then(async () => navigator.locks ? await navigator.locks.request('ft-summon-begins:' + eid, run) as string[] : await run());
  queues.set(eid, next); void next.finally(() => { if (queues.get(eid) === next) queues.delete(eid); }).catch(() => undefined); return next;
}
if (!(window as unknown as { __ftSummonBeginsMounted?: boolean }).__ftSummonBeginsMounted) {
  (window as unknown as { __ftSummonBeginsMounted?: boolean }).__ftSummonBeginsMounted = true;
  const seen = new Map<string, Set<string>>(), combats = new Map<string, string>(), pending = new Map<string, Set<string>>(), timers = new Map<string, number>();
  const observe = (eid: string, e: Row): void => {
    const prior = seen.get(eid), now = new Set(summonRoster(e).map(ecId)); seen.set(eid, now);
    const ids = pending.get(eid) ?? new Set<string>();
    for (const key of new Set([...heldVerdicts.keys(), ...settledArrivals.keys(), ...attempts.keys()])) {
      if (!key.startsWith(eid + '|')) continue;
      const id = key.slice(eid.length + 1), combat = heldVerdicts.get(key)?.combat ?? settledArrivals.get(key);
      if (!midCombatSummon(e, id) || (combat && combat !== upper(bucket(e).active_combat_id))) {
        clearArrival(key); settledArrivals.delete(key); ids.delete(id);
      }
    }
    for (const id of newMidCombatSummons(prior, e, combats.get(eid))) ids.add(id);
    combats.set(eid, upper(bucket(e).active_combat_id));
    pending.set(eid, ids);
    if (!ids.size) return;
    schedule(eid, ids, 1500);
  };
  // Retries are bounded in fireSummonBegins; pending ids only schedule delivery.
  const schedule = (eid: string, ids: Set<string>, delay: number): void => {
    clearTimeout(timers.get(eid)); timers.set(eid, window.setTimeout(() => {
      timers.delete(eid);
      const held = actorEncounterFor(eid) as Row | null; if (!held) { schedule(eid, ids, 3000); return; }
      const ready = [...ids].filter(id => { const s = midCombatSummon(held, id); return !!s && (mayAct(held, s) || heldVerdicts.has(eid + '|' + id)); });
      for (const id of [...ids]) if (!midCombatSummon(held, id)) { ids.delete(id); clearArrival(eid + '|' + id); settledArrivals.delete(eid + '|' + id); }
      if (!ready.length) return;
      void fireSummonBegins(eid, ready).then(again => {
        for (const id of ready) if (!again.includes(id)) ids.delete(id);
        if (ids.size) schedule(eid, ids, 3000);
      }).catch(error => { console.warn('[SUMMON-BEGINS]', error); schedule(eid, ids, 3000); });
    }, delay));
  };
  for (const [id, e] of window.FateTide?.encounterCache ?? []) if (record(e)) { seen.set(upper(id), new Set(summonRoster(e as unknown as Row).map(ecId))); combats.set(upper(id), upper(bucket(e as unknown as Row).active_combat_id)); }
  // Receipt alone precedes installation. Join the installed operation's identity before
  // retrying: a peer's applied aura must never be planned again as an arrival.
  window.addEventListener('ft:base-applied', event => {
    const frame = record(record((event as CustomEvent).detail)?.wire);
    if (frame?.kind !== 'applied') return;
    const eid = upper(frame.encounter_id), sid = upper(frame.actor_id), operation = upper(frame.operation_id);
    if (!UUID.test(eid) || !UUID.test(sid) || !UUID.test(operation)) return;
    const e = actorEncounterFor(eid) as Row | null;
    if (!e || !midCombatSummon(e, sid)) return;
    const key = eid + '|' + sid, combat = upper(bucket(e).active_combat_id);
    if (settledArrivals.get(key) === combat) return;
    const job = summonBeginsOperationId(sid).then(expected => {
      const now = actorEncounterFor(eid) as Row | null;
      if (operation !== expected || !now || !midCombatSummon(now, sid) || upper(bucket(now).active_combat_id) !== combat) return;
      settledArrivals.set(key, combat); clearArrival(key);
      const ids = pending.get(eid); ids?.delete(sid);
      if (!ids?.size) { clearTimeout(timers.get(eid)); timers.delete(eid); }
    }).catch(error => reportFailure(eid, sid, 'error', `summon-begins:installed-identity-failed:${String(error)}`));
    arrivalInstallations.set(key, job);
    void job.finally(() => { if (arrivalInstallations.get(key) === job) arrivalInstallations.delete(key); });
  });
  for (const name of ['ft:encounter-installed', 'ft:encounter-state-received', 'ft:encounter-rows-patched', 'ft:caster-state-applied']) window.addEventListener(name, event => {
    const d = record((event as CustomEvent).detail), installed = record(d?.encounterArr), eid = upper(d?.encounterId ?? (installed && bucket(installed).encounter_id));
    const e = actorEncounterFor(eid) as Row | null; if (e) observe(eid, e);
  });
  window.addEventListener('ft:legacy-action-accepted', event => {
    const d = record((event as CustomEvent).detail), data = record(d?.data), eid = upper(data?.encounter_id);
    if (!String(d?.post ?? '').includes('use_ability') || !UUID.test(eid)) return;
    void fetchAndCacheObject(eid, { fresh: true }).then(async value => {
      const e = record(value); if (!e || !record(e.encounters)) return;
      await handleEncounterUpdate({ type: 'encounter_update', encounter_arr: value as never }); observe(eid, e);
    }).catch(error => console.warn('[SUMMON-BEGINS]', error));
  });
}
